/* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- it can come as empty string */
import {
  getCurrentFormattedPrice,
  getOldPrice,
  mapCanBeSold,
  mapHasAutoSubscription,
  mapLabels,
  mapShade,
} from '@ori/product-box';
import { isNonNullable } from '@ori/ts-utils';

import type { ShadeWithPrice } from '../components';
import type { SearchPreviewProps } from '../components/SearchPreview/types';
import type { SearchPreviewQuery, SearchPreviewShadeFragment } from '../models/graphql-types/net';
import { mapEnableQuickBuyButton } from './mapEnableQuickBuyButton';

const mapShadeWithPrice = (shade: SearchPreviewShadeFragment): ShadeWithPrice => {
  const { productCode, hexColors, imageUrl } = mapShade({
    ...shade,
    productCode: shade.productCode ?? '',
  });

  return {
    name: shade.shadeName,
    productCode,
    hexColors,
    imageUrl,
    canBeSold: mapCanBeSold(shade.labels),
    price: {
      currentFormatted:
        shade.formattedPrice?.price?.priceAfterImmediateDiscount || shade.formattedPrice?.price?.currentPrice || '',
    },
  };
};
export const mapSearchPreviewShades = (
  shades?: ReadonlyArray<SearchPreviewShadeFragment | null> | null,
): ShadeWithPrice[] => shades?.filter(isNonNullable).map(mapShadeWithPrice) || [];

interface GetPriceParams {
  priceAfterImmediateDiscount?: unknown;
  currentPrice?: unknown;
}
export const getPrice = ({ priceAfterImmediateDiscount, currentPrice }: GetPriceParams): number =>
  Number.parseFloat((priceAfterImmediateDiscount ?? currentPrice ?? 0).toString());

export type SearchPreviewResult = Pick<SearchPreviewProps, 'products' | 'suggestions' | 'numberOfProducts'>;
export const mapSearchPreviewQuery = (query: SearchPreviewQuery): SearchPreviewResult => {
  const { totalProductsCount, products, suggestions } = query.application?.productSearchPreview?.searchResults ?? {};

  return {
    numberOfProducts: totalProductsCount ?? 0,
    products:
      products?.filter(isNonNullable).map((product) => {
        const shades = mapSearchPreviewShades(product.concept?.products);
        const labels = mapLabels(product.concept?.labels, { deal: [] });

        return {
          isMultishade: shades.length > 1,
          canBeSold: mapEnableQuickBuyButton(product.labels, shades),
          url: product.pdpUrl?.relativeUrl,
          code: product.productCode ?? '',
          name: product.concept?.name ?? '',
          brand: product.concept?.brand?.name,
          image: product.imageUrl,
          deal: labels?.deal,
          ppa: labels?.ppa,
          category: product.concept?.categories?.[0]?.name,
          hasAutosubscribe: mapHasAutoSubscription(product.labels),
          price: {
            currentFormatted: getCurrentFormattedPrice({ formattedPrice: product.formattedPrice }),
            oldFormatted: getOldPrice({ formattedPrice: product.formattedPrice }),
            price: getPrice({
              priceAfterImmediateDiscount: product.price?.price?.priceAfterImmediateDiscount,
              currentPrice: product.price?.price?.currentPrice,
            }),
          },
          shades,
        };
      }) ?? [],
    suggestions: suggestions?.filter(isNonNullable).map((text) => ({ text })) ?? [],
  };
};
